import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select as MuiSelect, styled } from "@mui/material";
import { Field, useForm } from "react-final-form";

import { TextField } from "~ui/atoms/Form";
import { validateOptionalUsPhone } from "~utils/form/validations";
import {
  DEFAULT_PHONE_COUNTRY_ISO,
  formatCountryOptionLabel,
  getPhoneCountry,
  isUsPhoneCountry,
  PHONE_COUNTRIES,
} from "~utils/phone/countries";

export const FIELD_NAME_PHONE_COUNTRY_CODE = "phoneCountryCode";
export const FIELD_NAME_PHONE_NUMBER = "phoneNumber";

const tCountryLabel = "Country";
const tUsOnlyMessage = "We only support US phone numbers for texting right now.";

const Row = styled(Box)(function ({ theme }) {
  return {
    alignItems: "flex-start",
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "minmax(11rem, 0.45fr) minmax(0, 1fr)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
    width: "100%",
  };
});

export interface PhoneWithCountryCodeProps {
  countryFieldName?: string;
  phoneFieldName?: string;
  phoneLabel?: string;
  initialCountryCode?: string;
  initialPhoneNumber?: string;
  required?: boolean;
  variant?: "filled" | "outlined" | "standard";
  /** data-testid / data-cy prefix helpers */
  phoneTestId?: string;
}

/**
 * Country dial-code dropdown + national phone number field.
 * Non-US countries may be selected for familiarity, but entering a number
 * while a non-US country is selected is blocked (US texting only).
 */
export function PhoneWithCountryCode({
  countryFieldName = FIELD_NAME_PHONE_COUNTRY_CODE,
  phoneFieldName = FIELD_NAME_PHONE_NUMBER,
  phoneLabel = "Mobile Phone Number",
  initialCountryCode = DEFAULT_PHONE_COUNTRY_ISO,
  initialPhoneNumber,
  required = false,
  variant = "filled",
  phoneTestId,
}: PhoneWithCountryCodeProps) {
  const form = useForm();

  return (
    <Row>
      <Field
        initialValue={initialCountryCode || DEFAULT_PHONE_COUNTRY_ISO}
        name={countryFieldName}
        render={({ input, meta }) => {
          const country = getPhoneCountry(input.value);
          const showUsOnlyHint = !isUsPhoneCountry(input.value);
          return (
            <FormControl error={Boolean(meta.error)} fullWidth variant={variant}>
              <InputLabel id={`${countryFieldName}-label`}>{tCountryLabel}</InputLabel>
              <MuiSelect
                label={tCountryLabel}
                labelId={`${countryFieldName}-label`}
                name={input.name}
                onBlur={input.onBlur}
                onChange={(event) => {
                  input.onChange(event.target.value);
                  form.blur(phoneFieldName);
                }}
                onFocus={input.onFocus}
                renderValue={(value) => {
                  const selected = getPhoneCountry(String(value));
                  return `${selected.flag} ${selected.dialCode}`;
                }}
                value={country.iso}
                variant={variant}
              >
                {PHONE_COUNTRIES.map((item) => (
                  <MenuItem key={item.iso} value={item.iso}>
                    {formatCountryOptionLabel(item)}
                  </MenuItem>
                ))}
              </MuiSelect>
              {showUsOnlyHint ? <FormHelperText>{tUsOnlyMessage}</FormHelperText> : null}
            </FormControl>
          );
        }}
        validate={(value, allValues) => {
          const phone =
            typeof allValues === "object" && allValues != null && phoneFieldName in allValues
              ? String((allValues as Record<string, unknown>)[phoneFieldName] ?? "").trim()
              : "";
          if (phone && value !== DEFAULT_PHONE_COUNTRY_ISO) {
            return tUsOnlyMessage;
          }
        }}
      />
      <TextField
        initialValue={initialPhoneNumber}
        name={phoneFieldName}
        required={required}
        textFieldProps={{
          "data-cy": phoneTestId,
          "data-testid": phoneTestId ?? phoneFieldName,
          label: phoneLabel,
          placeholder: "(555) 867-5309",
          variant,
        }}
        type="tel"
        validate={validateOptionalUsPhone}
      />
    </Row>
  );
}
